import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiShare2, FiPlus, FiTrash2, FiRefreshCw, FiX, FiCheck, FiHelpCircle, FiSearch, FiArrowRight } from 'react-icons/fi';
import { ROUTES } from '../../app/routes';
import { getStatus, getAgentMcpServers, getMcpCredentials, getExternalMcpList, getToolApprovals, saveToolApprovals, assignMcpServer, removeMcpServer, listDwAgents, type McpServerRecord, type McpCredential, type ExternalMcpItem, type ToolApprovalMode, } from './claudeAgentApi';
import { dedupeExternalMcps } from '../../services/marketplaceService';
import ClaudeAgentRequestPage from './ClaudeAgentRequestPage';
import ClaudeAgentPendingPage from './ClaudeAgentPendingPage';
type RightPanel = {
    mode: 'empty';
} | {
    mode: 'view-connected';
    server: McpServerRecord;
    tools: Array<{
        Name: string;
        Description?: string;
    }>;
} | {
    mode: 'view-available';
    item: ExternalMcpItem;
    tools: Array<{
        Name: string;
        Description?: string;
    }>;
};
const ClaudeAgentMcpPage: React.FC = () => {
    const navigate = useNavigate();
    const [agentId, setAgentId] = useState<string | null>(null);
    const [loadingAgent, setLoadingAgent] = useState(true);
    const [configured, setConfigured] = useState<boolean | null>(null);
    const [localGate, setLocalGate] = useState<'none' | 'pending'>('none');
    const [connected, setConnected] = useState<McpServerRecord[]>([]);
    const [creds, setCreds] = useState<McpCredential[]>([]);
    const [external, setExternal] = useState<ExternalMcpItem[]>([]);
    const [toolApprovals, setToolApprovals] = useState<Record<string, ToolApprovalMode>>({});
    const [dataReady, setDataReady] = useState(false);
    const [search, setSearch] = useState('');
    const [panel, setPanel] = useState<RightPanel>({ mode: 'empty' });
    const [confirmRm, setConfirmRm] = useState<number | null>(null);
    const [removingId, setRemovingId] = useState<number | null>(null);
    const [connectingId, setConnectingId] = useState<string | null>(null);
    useEffect(() => {
        Promise.all([
            getStatus().catch(() => null),
            getExternalMcpList().catch(() => [] as ExternalMcpItem[]),
        ]).then(async ([st, ext]) => {
            setConfigured(st?.configured ?? false);
            setExternal(dedupeExternalMcps(ext));
            if (!st?.configured)
                return;
            const agents = await listDwAgents().catch(() => []);
            const resolvedAgentId = agents[0]?.agent_id;
            if (resolvedAgentId) {
                setAgentId(resolvedAgentId);
                return Promise.all([
                    getAgentMcpServers(resolvedAgentId).catch(() => [] as McpServerRecord[]),
                    getMcpCredentials().catch(() => [] as McpCredential[]),
                    getToolApprovals(resolvedAgentId).catch(() => ({} as Record<string, ToolApprovalMode>)),
                ]).then(([srv, cr, ta]) => {
                    setConnected(srv);
                    setCreds(cr);
                    setToolApprovals(ta);
                    if (srv.length > 0) {
                        const first = srv[0];
                        const extItem = ext.find(e => e.url === first.url);
                        const tools = extItem?.tools ? (() => { try {
                            return JSON.parse(extItem.tools);
                        }
                        catch {
                            return [];
                        } })() : [];
                        setPanel({ mode: 'view-connected', server: first, tools });
                    }
                });
            }
        })
            .catch(() => { })
            .finally(() => { setDataReady(true); setLoadingAgent(false); });
    }, []);
    const connectedUrls = new Set(connected.map(s => s.url));
    function parsedTools(raw?: string): Array<{
        Name: string;
        Description?: string;
    }> {
        try {
            return raw ? JSON.parse(raw) : [];
        }
        catch {
            return [];
        }
    }
    function matchSearch(name: string, desc?: string) {
        if (!search.trim())
            return true;
        const q = search.toLowerCase();
        return name.toLowerCase().includes(q) || (desc ?? '').toLowerCase().includes(q);
    }
    const filteredConnected = connected.filter(s => matchSearch(s.name, s.url));
    const filteredExternal = external.filter(i => matchSearch(i.name, i.description));
    const handleApprovalChange = (serverName: string, toolName: string, mode: ToolApprovalMode) => {
        if (!agentId)
            return;
        const key = `${serverName}::${toolName}`;
        const updated = { ...toolApprovals, [key]: mode };
        setToolApprovals(updated);
        saveToolApprovals(agentId, updated).catch(() => { });
    };
    const handleConnect = async (item: ExternalMcpItem) => {
        if (!agentId || connectingId)
            return;
        setConnectingId(item.transactionId);
        try {
            await assignMcpServer(agentId, item.name, item.url, item.transactionId);
            const updated = await getAgentMcpServers(agentId);
            setConnected(updated);
            if (panel.mode === 'view-available' && panel.item.transactionId === item.transactionId) {
                const srv = updated.find(s => s.url === item.url);
                if (srv)
                    setPanel({ mode: 'view-connected', server: srv, tools: parsedTools(item.tools) });
            }
        }
        catch { }
        finally {
            setConnectingId(null);
        }
    };
    const handleRemove = async (id: number) => {
        if (!agentId)
            return;
        setRemovingId(id);
        try {
            await removeMcpServer(agentId, id);
            setConnected(prev => prev.filter(s => s.id !== id));
            if (panel.mode === 'view-connected' && panel.server.id === id)
                setPanel({ mode: 'empty' });
        }
        catch { }
        finally {
            setRemovingId(null);
            setConfirmRm(null);
        }
    };
    if (!loadingAgent && configured === false) {
        if (localGate === 'pending')
            return <ClaudeAgentPendingPage />;
        return (<ClaudeAgentRequestPage onActivated={() => window.location.reload()} onPending={() => setLocalGate('pending')}/>);
    }
    if (loadingAgent || !dataReady) {
        return (<div className="ca-fp-page">
        <div className="ca-fp-left">
          <div className="ca-fp-left__header">
            <div className="ca-fp-skel-bone" style={{ width: 80, height: 18, borderRadius: 6 }}/>
            <div className="ca-fp-skel-bone" style={{ width: 70, height: 28, borderRadius: 8 }}/>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '12px 12px 0' }}>
            {[0, 1, 2, 3].map(i => (<div key={i} className="ca-fp-skill-row" style={{ pointerEvents: 'none' }}>
                <div className="ca-fp-skel-bone" style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }}/>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div className="ca-fp-skel-bone" style={{ width: '60%', height: 11 }}/>
                  <div className="ca-fp-skel-bone" style={{ width: '80%', height: 8, borderRadius: 4 }}/>
                </div>
              </div>))}
          </div>
        </div>
        <div className="ca-fp-right">
          <div className="ca-fp-empty-state">
            <FiShare2 size={36} style={{ opacity: 0.18 }}/>
            <span>Loading MCP servers…</span>
          </div>
        </div>
      </div>);
    }
    return (<div className="ca-fp-page">
      
      <div className="ca-fp-left">
        <div className="ca-fp-left__header">
          <h2 className="ca-fp-left__title">
            <FiShare2 size={14} style={{ color: '#00b0d3' }}/> MCP
            <span className="ca-fp-left__count">{connected.length}</span>
          </h2>
        </div>

        
        <div className="ca-fp-left__search-wrap">
          <FiSearch size={13} className="ca-fp-left__search-icon"/>
          <input className="ca-fp-left__search" placeholder="Search servers…" value={search} onChange={e => setSearch(e.target.value)}/>
          {search && (<button className="ca-fp-left__search-clear" onClick={() => setSearch('')}>
              <FiX size={11}/>
            </button>)}
        </div>

        <div className="ca-fp-left__list">
          
          {filteredConnected.length > 0 && (<>
              <div className="ca-fp-group-head">
                <span>Connected</span>
                <span className="ca-fp-group-head__badge">{filteredConnected.length}</span>
              </div>
              {filteredConnected.map(s => {
                const extItem = external.find(e => e.url === s.url);
                const tools = parsedTools(extItem?.tools);
                const isActive = panel.mode === 'view-connected' && panel.server.id === s.id;
                return (<div key={s.id} className={`ca-fp-skill-row ca-fp-skill-row--assigned${isActive ? ' ca-fp-skill-row--active' : ''}`} onClick={() => setPanel({ mode: 'view-connected', server: s, tools })}>
                    <div className="ca-fp-mcp-icon"><FiShare2 size={13}/></div>
                    <div className="ca-fp-skill-row__info">
                      <span className="ca-fp-skill-row__name">{s.name}</span>
                      <span className="ca-fp-skill-row__ver" style={{ fontFamily: 'monospace', fontSize: 9 }}>{s.url.slice(0, 38)}{s.url.length > 38 ? '…' : ''}</span>
                    </div>
                    <span className="ca-fp-skill-row__dot" title="Connected"/>
                  </div>);
            })}
            </>)}

          
          {filteredExternal.length > 0 && (<>
              <div className="ca-fp-group-head" style={{ marginTop: connected.length > 0 ? 12 : 0 }}>
                <span>Available</span>
                <span className="ca-fp-group-head__badge">{filteredExternal.length}</span>
              </div>
              {filteredExternal.map(item => {
                const isConnected = connectedUrls.has(item.url);
                const isActive = panel.mode === 'view-available' && panel.item.transactionId === item.transactionId;
                const tools = parsedTools(item.tools);
                return (<div key={item.transactionId} className={`ca-fp-skill-row${isConnected ? ' ca-fp-skill-row--assigned' : ''}${isActive ? ' ca-fp-skill-row--active' : ''}`} onClick={() => {
                        if (isConnected) {
                            const srv = connected.find(s => s.url === item.url);
                            if (srv)
                                setPanel({ mode: 'view-connected', server: srv, tools });
                        }
                        else {
                            setPanel({ mode: 'view-available', item, tools });
                        }
                    }}>
                    <div className="ca-fp-mcp-icon"><FiShare2 size={13}/></div>
                    <div className="ca-fp-skill-row__info">
                      <span className="ca-fp-skill-row__name">{item.name}</span>
                      {item.description && <span className="ca-fp-skill-row__desc">{item.description}</span>}
                    </div>
                    {item.needsUserCredentials && (<span title="You need to set up your personal credentials for this tool" style={{
                            fontSize: 11, fontWeight: 600, color: '#fbbf24', background: 'rgba(245,158,11,0.12)',
                            border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, padding: '1px 6px', marginRight: 6,
                        }}>
                        🔑 Needs credentials
                      </span>)}
                    {isConnected && <span className="ca-fp-skill-row__dot" title="Connected"/>}
                    {tools.length > 0 && (<span className="ca-fp-mcp-tool-count">{tools.length}</span>)}
                  </div>);
            })}
            </>)}

          {filteredConnected.length === 0 && filteredExternal.length === 0 && (<div className="ca-fp-left__empty">
              {search ? 'No servers match your search.' : 'No MCP servers found.'}
            </div>)}
        </div>

        
        <div className="ca-fp-mcp-marketplace-cta">
          <p className="ca-fp-mcp-marketplace-cta__text">
            Want to add a new MCP server? Create and manage your MCP servers from My Tools.
          </p>
          <button className="ca-fp-mcp-marketplace-cta__btn" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=marketplace`)}>
            Go to My Tools <FiArrowRight size={12}/>
          </button>
        </div>
      </div>

      
      <div className="ca-fp-right">

        
        {panel.mode === 'empty' && (<div className="ca-fp-empty-state">
            <div className="ca-fp-empty-state__icon"><FiShare2 size={32}/></div>
            <h3>Select a server to view its tools</h3>
            <p>Choose a connected or available server from the left panel.</p>
            <button className="ca-fp-mcp-marketplace-cta__btn ca-fp-mcp-marketplace-cta__btn--lg" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=marketplace`)}>
              Go to My Tools <FiArrowRight size={13}/>
            </button>
          </div>)}

        
        {panel.mode === 'view-connected' && (() => {
            const { server: s, tools } = panel;
            const isRemoving = removingId === s.id;
            return (<div className="ca-fp-editor">
              <div className="ca-fp-editor__topbar">
                <div className="ca-fp-editor__topbar-left">
                  <div className="ca-fp-mcp-icon ca-fp-mcp-icon--lg ca-fp-mcp-icon--connected">
                    <FiShare2 size={18}/>
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="ca-fp-editor__skill-title">{s.name}</span>
                      <span className="ca-fp-editor__badge ca-fp-editor__badge--assigned">Connected</span>
                    </div>
                    <span className="ca-fp-editor__skill-sub" style={{ fontFamily: 'monospace', fontSize: 10 }}>{s.url}</span>
                  </div>
                </div>
                <div className="ca-fp-editor__topbar-actions">
                  {confirmRm === s.id ? (<>
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Disconnect?</span>
                      <button className="ca-fp-editor__btn ca-fp-editor__btn--danger" onClick={() => handleRemove(s.id)} disabled={isRemoving}>
                        {isRemoving ? <FiRefreshCw size={12} className="ca-fp-spin"/> : 'Yes, Remove'}
                      </button>
                      <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setConfirmRm(null)}>No</button>
                    </>) : (<button className="ca-fp-editor__btn ca-fp-editor__btn--danger-ghost" onClick={() => setConfirmRm(s.id)}>
                      <FiTrash2 size={12}/> Disconnect
                    </button>)}
                </div>
              </div>

              
              <div className="ca-fp-editor__body">
                <div className="ca-fp-mcp-section-head">
                  <span>Tools</span>
                  <span className="ca-fp-group-head__badge">{tools.length}</span>
                </div>
                {tools.length === 0 ? (<p className="ca-fp-editor__readonly-msg">No tool definitions available for this server.</p>) : (<div className="ca-fp-mcp-tools-grid">
                    {tools.map((t, i) => {
                        const key = `${s.name}::${t.Name}`;
                        const mode = toolApprovals[key] ?? 'ask';
                        return (<div key={i} className="ca-fp-mcp-tool-card">
                          <div className="ca-fp-mcp-tool-card__meta">
                            <span className="ca-fp-mcp-tool-card__name">{t.Name}</span>
                            {t.Description && (<span className="ca-fp-mcp-tool-card__desc">
                                {t.Description.split('\n')[0].slice(0, 120)}{t.Description.length > 120 ? '…' : ''}
                              </span>)}
                          </div>
                          <div className="ca-fp-tool-approval">
                            <button className={`ca-fp-tool-approval__btn ca-fp-tool-approval__btn--ask${mode === 'ask' ? ' active' : ''}`} onClick={() => handleApprovalChange(s.name, t.Name, 'ask')} title="Ask before running">
                              <FiHelpCircle size={11}/>
                            </button>
                            <button className={`ca-fp-tool-approval__btn ca-fp-tool-approval__btn--approved${mode === 'approved' ? ' active' : ''}`} onClick={() => handleApprovalChange(s.name, t.Name, 'approved')} title="Auto-approve always">
                              <FiCheck size={11}/>
                            </button>
                            <button className={`ca-fp-tool-approval__btn ca-fp-tool-approval__btn--restrict${mode === 'restrict' ? ' active' : ''}`} onClick={() => handleApprovalChange(s.name, t.Name, 'restrict')} title="Always block">
                              <FiX size={11}/>
                            </button>
                          </div>
                        </div>);
                    })}
                  </div>)}
              </div>
            </div>);
        })()}

        
        {panel.mode === 'view-available' && (() => {
            const { item, tools } = panel;
            const isConnected = connectedUrls.has(item.url);
            const isConnecting = connectingId === item.transactionId;
            return (<div className="ca-fp-editor">
              <div className="ca-fp-editor__topbar">
                <div className="ca-fp-editor__topbar-left">
                  <div className="ca-fp-mcp-icon ca-fp-mcp-icon--lg"><FiShare2 size={18}/></div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="ca-fp-editor__skill-title">{item.name}</span>
                      <span className="ca-fp-editor__badge ca-fp-editor__badge--premade">Platform</span>
                      {isConnected && <span className="ca-fp-editor__badge ca-fp-editor__badge--assigned">Connected</span>}
                    </div>
                    {item.description && <span className="ca-fp-editor__skill-sub">{item.description}</span>}
                  </div>
                </div>
                <div className="ca-fp-editor__topbar-actions">
                  {!isConnected && (<button className="ca-fp-editor__btn ca-fp-editor__btn--assign" style={{ background: 'rgba(0,176,211,0.12)', borderColor: 'rgba(0,176,211,0.3)', color: '#00b0d3' }} onClick={() => handleConnect(item)} disabled={!!connectingId}>
                      {isConnecting
                        ? <><FiRefreshCw size={12} className="ca-fp-spin"/> Connecting…</>
                        : <><FiPlus size={12}/> Connect Server</>}
                    </button>)}
                  {isConnected && (() => {
                    const srv = connected.find(s => s.url === item.url);
                    if (!srv)
                        return null;
                    return confirmRm === srv.id ? (<>
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Disconnect?</span>
                        <button className="ca-fp-editor__btn ca-fp-editor__btn--danger" onClick={() => handleRemove(srv.id)}>Yes</button>
                        <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setConfirmRm(null)}>No</button>
                      </>) : (<button className="ca-fp-editor__btn ca-fp-editor__btn--danger-ghost" onClick={() => setConfirmRm(srv.id)}>
                        <FiTrash2 size={12}/> Disconnect
                      </button>);
                })()}
                </div>
              </div>

              <div className="ca-fp-editor__body">
                <div className="ca-fp-mcp-section-head">
                  <span>Available Tools</span>
                  <span className="ca-fp-group-head__badge">{tools.length}</span>
                </div>
                {tools.length === 0 ? (<p className="ca-fp-editor__readonly-msg">No tool definitions available for this server.</p>) : (<div className="ca-fp-mcp-tools-grid">
                    {tools.map((t, i) => (<div key={i} className="ca-fp-mcp-tool-card ca-fp-mcp-tool-card--preview">
                        <div className="ca-fp-mcp-tool-card__meta">
                          <span className="ca-fp-mcp-tool-card__name">{t.Name}</span>
                          {t.Description && (<span className="ca-fp-mcp-tool-card__desc">
                              {t.Description.split('\n')[0].slice(0, 120)}{t.Description.length > 120 ? '…' : ''}
                            </span>)}
                        </div>
                      </div>))}
                  </div>)}

                
                <div className="ca-fp-mcp-section-head" style={{ marginTop: 20 }}>
                  <span>Endpoint</span>
                </div>
                <code className="ca-fp-mcp-endpoint">{item.url}</code>
              </div>
            </div>);
        })()}
      </div>
    </div>);
};
export default ClaudeAgentMcpPage;
